/**
 * Form body for the offer edit dialog. Holds Name/URL/Country/Group/AN +
 * token chips + payout fields. The shell dialog stays small.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { AlertTriangle } from 'lucide-react';

import { Input } from '@/components/ui/form/input/Input';
import { SearchableSelect } from '@/components/ui/form/input/SearchableSelect';
import type { BinomOfferDetail } from '@/hooks/binom';

import OfferEditField from '../OfferEditField';
import BinomUrlTokensPanel from '../tokens/BinomUrlTokensPanel';

import BinomOfferPayoutFields from './BinomOfferPayoutFields';

export interface BinomOfferEditBodyProps {
  form: BinomOfferDetail;
  onChange: (next: BinomOfferDetail) => void;
  countryOptions: Array<{ value: string; label: string }>;
  groupOptions: Array<{ value: string; label: string }>;
  networkOptions: Array<{ value: number | string; label: string }>;
}

const BinomOfferEditBody: FC<BinomOfferEditBodyProps> = ({
  form,
  onChange,
  countryOptions,
  groupOptions,
  networkOptions,
}) => (
  <div className="space-y-3">
    <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/5 p-2 text-[11px] text-warning">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
      <span>
        This edits the GLOBAL offer entity in Binom — every campaign / path
        using it picks up the change.
      </span>
    </div>
    <OfferEditField label="Name">
      <Input
        value={form.name ?? ''}
        onChange={(e) => onChange({ ...form, name: e.target.value } as BinomOfferDetail)}
        className="h-8 text-xs"
      />
    </OfferEditField>
    <OfferEditField label="URL">
      <div className="flex flex-col gap-1">
        <Input
          value={form.url ?? ''}
          onChange={(e) => onChange({ ...form, url: e.target.value } as BinomOfferDetail)}
          className="h-8 text-xs"
        />
        <BinomUrlTokensPanel
          url={form.url ?? ''}
          onUrlChange={(next) => onChange({ ...form, url: next } as BinomOfferDetail)}
        />
      </div>
    </OfferEditField>
    <OfferEditField label="Country">
      <SearchableSelect
        value={form.countryCode ?? ''}
        onChange={(v) =>
          onChange({ ...form, countryCode: String(v) } as BinomOfferDetail)
        }
        options={[{ value: '', label: 'No country' }, ...countryOptions]}
      />
    </OfferEditField>
    <OfferEditField label="Group">
      <SearchableSelect
        value={form.groupUuid ?? ''}
        onChange={(v) =>
          onChange({ ...form, groupUuid: String(v) } as BinomOfferDetail)
        }
        options={[{ value: '', label: 'No group' }, ...groupOptions]}
      />
    </OfferEditField>
    <OfferEditField label="Aff. network">
      <SearchableSelect
        value={String(form.affiliateNetworkId ?? '')}
        onChange={(v) =>
          onChange({
            ...form,
            affiliateNetworkId: v ? Number(v) || String(v) : undefined,
          } as BinomOfferDetail)
        }
        options={[{ value: '', label: 'No network' }, ...networkOptions]}
      />
    </OfferEditField>
    <BinomOfferPayoutFields form={form} onChange={onChange} />
  </div>
);

export default BinomOfferEditBody;
